

/**
 * Normalize persisted GraphQL body (object or JSON string) for the editor.
 */
export function normalizeGraphQLBody(body) {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return {
      query: typeof body.query === 'string' ? body.query : '',
      variables: typeof body.variables === 'string' ? body.variables : JSON.stringify(body.variables ?? {}, null, 2),
      operationName: typeof body.operationName === 'string' ? body.operationName : '',
    };
  }
  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object') {
        return normalizeGraphQLBody(parsed);
      }
    } catch {
      /* plain string — treat as raw query text */
      return { query: body, variables: '{}', operationName: '' };
    }
  }
  return { query: '', variables: '{}', operationName: '' };
}

// ── Auto-introspection policy (COL-04) ───────────────────────────────────────

/**
 * Whether a freshly-opened GraphQL request tab introspects on its own.
 *
 * This used to be `true`, which made opening a request the same thing as
 * sending one: 500 ms after a *click on a sidebar node* — no Send — the app
 * POSTed a full introspection query to whatever URL the request carried,
 * with every enabled header row already `{{variable}}`-substituted. For a
 * request that arrived inside an imported or shared collection that meant a
 * header row of `Authorization: Bearer {{PROD_TOKEN}}` was delivered to the
 * collection author's host on tab open, and again on tab restore at app
 * start, and again whenever the resolved URL changed.
 *
 * Opt-in instead of opt-out. The convenience is preserved rather than
 * removed: the schema picker still has "Auto Fetch", and the refresh button
 * next to it fetches once on demand — both are explicit user actions on a
 * request the user is already looking at, which is the property the default
 * lacked. Provenance would be the more surgical gate ("auto-fetch only for
 * requests the user created"), but a request's provenance travels inside the
 * same document the attacker writes, so it cannot be trusted to fail closed;
 * a deterministic default can.
 */
export const GQL_AUTO_FETCH_DEFAULT = false;

/**
 * The whole auto-introspection gate, as one testable predicate. Any `false`
 * here means no unrequested HTTP request leaves the app.
 */
export function shouldAutoIntrospect({
  autoFetch,
  bodyType,
  url,
  resolvedUrl,
  lastFetchedUrl,
} = {}) {
  if (!autoFetch) return false;
  if (bodyType !== 'graphql') return false;
  if (!url || !String(url).trim()) return false;
  if (!resolvedUrl) return false;
  if (resolvedUrl === lastFetchedUrl) return false;
  return true;
}
